import { useMemo, useState } from "react";

import {
    Box,
    Dialog,
    DialogContent,
    DialogTitle,
    Divider,
    Stack,
    ToggleButton,
    ToggleButtonGroup,
    Typography,
} from "@mui/material";

import { dayjsUTC } from "../../helpers/dates";
import EventsTable from "./EventsTable";

const MAX_TABLE_HEIGHT = 400;

const dayjs = dayjsUTC;

const EventsComparison = ({
    open,
    onClose,
    events,
    eventsExtra,
    loading,
    handleDelete,
    handleEventClicked,
    baseFromDate,
    baseToDate,
    isAdmin = false,
}) => {
    const [comparisonView, setComparisonView] = useState("both");
    const hasSelectedRange = baseToDate !== undefined && baseToDate !== null;

    const comparisonMonth = dayjs(baseFromDate ?? dayjs());
    const comparisonCurrentYear = comparisonMonth.year();

    const rangeStartBase = dayjs(baseFromDate ?? dayjs());
    const rangeEndBase = dayjs(baseToDate ?? baseFromDate ?? dayjs());

    const normalizedRange = rangeStartBase.isAfter(rangeEndBase, "day")
        ? { start: rangeEndBase, end: rangeStartBase }
        : { start: rangeStartBase, end: rangeEndBase };

    const eventOverlapsPeriod = (event, start, end) => {
        const eventStart = dayjs(event.FromDate);
        const eventEnd = dayjs(event.ToDate);

        return (
            eventStart.isBetween(start, end, "day", "[]") ||
            eventEnd.isBetween(start, end, "day", "[]") ||
            (eventStart.isBefore(start, "day") && eventEnd.isAfter(end, "day"))
        );
    };

    const currentYearPeriod = hasSelectedRange
        ? {
              start: normalizedRange.start,
              end: normalizedRange.end,
          }
        : {
              start: comparisonMonth
                  .year(comparisonCurrentYear)
                  .startOf("month"),
              end: comparisonMonth.year(comparisonCurrentYear).endOf("month"),
          };

    const previousYearPeriod = {
        start: currentYearPeriod.start.subtract(1, "year"),
        end: currentYearPeriod.end.subtract(1, "year"),
    };

    const formatPeriodLabel = (start, end) => {
        const sameYear = start.isSame(end, "year");

        return sameYear
            ? `${start.format("DD MMM")} - ${end.format("DD MMM YYYY")}`
            : `${start.format("DD MMM YYYY")} - ${end.format("DD MMM YYYY")}`;
    };

    const currentPeriodLabel = formatPeriodLabel(
        currentYearPeriod.start,
        currentYearPeriod.end,
    );
    const previousPeriodLabel = formatPeriodLabel(
        previousYearPeriod.start,
        previousYearPeriod.end,
    );

    const handleComparisonEventClicked = (event, mode = "view") => {
        handleEventClicked(event, mode);
    };

    const currentYearEvents = useMemo(
        () =>
            events.filter((event) =>
                eventOverlapsPeriod(
                    event,
                    currentYearPeriod.start,
                    currentYearPeriod.end,
                ),
            ),
        [events, currentYearPeriod.start, currentYearPeriod.end],
    );

    const previousYearEvents = useMemo(
        () =>
            events.filter((event) =>
                eventOverlapsPeriod(
                    event,
                    previousYearPeriod.start,
                    previousYearPeriod.end,
                ),
            ),
        [events, previousYearPeriod.start, previousYearPeriod.end],
    );

    if (!isAdmin) return null;

    return (
        <Dialog open={open} onClose={onClose} fullWidth maxWidth="lg">
            <DialogTitle>
                <Typography component="div" variant="h6">
                    {currentPeriodLabel}
                    <Box component="span" sx={{ px: 1, fontWeight: "bold" }}>
                        —
                    </Box>
                    {previousPeriodLabel}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                    Current period vs the same period last year.
                </Typography>
                <ToggleButtonGroup
                    exclusive
                    value={comparisonView}
                    onChange={(_, nextView) => {
                        if (nextView) setComparisonView(nextView);
                    }}
                    size="small"
                    color="primary"
                    sx={{ mt: 1 }}
                >
                    <ToggleButton value="both">Both</ToggleButton>
                    <ToggleButton value="current">Current</ToggleButton>
                    <ToggleButton value="previous">Last Year</ToggleButton>
                </ToggleButtonGroup>
            </DialogTitle>
            <DialogContent>
                <Stack spacing={2}>
                    {(comparisonView === "both" ||
                        comparisonView === "current") && (
                        <>
                            <Stack
                                direction="row"
                                justifyContent="space-between"
                                alignItems="center"
                            >
                                <Box>
                                    <Typography variant="h6">
                                        Current
                                    </Typography>
                                    <Typography
                                        variant="body2"
                                        color="text.secondary"
                                    >
                                        {currentPeriodLabel}
                                    </Typography>
                                </Box>
                                <Box
                                    sx={{
                                        px: 1.5,
                                        py: 0.25,
                                        borderRadius: 10,
                                        border: "1px solid",
                                        borderColor: "grey.400",
                                    }}
                                >
                                    <Typography
                                        variant="body2"
                                        color="text.secondary"
                                    >
                                        {currentYearEvents.length}{" "}
                                        {currentYearEvents.length === 1
                                            ? "event"
                                            : "events"}
                                    </Typography>
                                </Box>
                            </Stack>
                            <EventsTable
                                handleDelete={handleDelete}
                                eventsExtra={eventsExtra}
                                events={currentYearEvents}
                                loading={loading}
                                handleEventClicked={
                                    handleComparisonEventClicked
                                }
                                showActions={false}
                                maxHeight={MAX_TABLE_HEIGHT}
                                emptyMessage="No events available for comparison."
                            />
                        </>
                    )}

                    {(comparisonView === "both" ||
                        comparisonView === "previous") && (
                        <>
                            {comparisonView === "both" && <Divider />}
                            <Stack
                                direction="row"
                                justifyContent="space-between"
                                alignItems="center"
                            >
                                <Box>
                                    <Typography variant="h6">
                                        Last Year
                                    </Typography>
                                    <Typography
                                        variant="body2"
                                        color="text.secondary"
                                    >
                                        {previousPeriodLabel}
                                    </Typography>
                                </Box>
                                <Box
                                    sx={{
                                        px: 1.5,
                                        py: 0.25,
                                        borderRadius: 10,
                                        border: "1px solid",
                                        borderColor: "grey.400",
                                    }}
                                >
                                    <Typography
                                        variant="body2"
                                        color="text.secondary"
                                    >
                                        {previousYearEvents.length}{" "}
                                        {previousYearEvents.length === 1
                                            ? "event"
                                            : "events"}
                                    </Typography>
                                </Box>
                            </Stack>
                            <EventsTable
                                handleDelete={handleDelete}
                                eventsExtra={eventsExtra}
                                events={previousYearEvents}
                                loading={loading}
                                handleEventClicked={
                                    handleComparisonEventClicked
                                }
                                showActions={false}
                                maxHeight={MAX_TABLE_HEIGHT}
                                emptyMessage="No events available for comparison."
                            />
                        </>
                    )}
                    <Typography
                        variant="body2"
                        color="text.secondary"
                        align="center"
                        sx={{ pt: 1 }}
                    >
                        {currentYearEvents.length} current ·{" "}
                        {previousYearEvents.length} last year
                    </Typography>
                </Stack>
            </DialogContent>
        </Dialog>
    );
};

export default EventsComparison;
